import {
  APPEAR_ANIMATIONS,
  BREAKPOINTS,
  bindNestedLinks,
  createAnimator,
} from './home-framer-runtime'
import { CIRCUIT_ANIM } from './circuit-anim-frames'

const HERO_WORDS = ['CRYPTO', 'STOCKS', 'COMMODITIES']
const HERO_INTERVAL_MS = 1800

const TAPE_ROWS = [
  { side: 'buy', amount: '1.24', coin: 'BTC' },
  { side: 'sell', amount: '18.6', coin: 'ETH' },
  { side: 'buy', amount: '420', coin: 'SOL' },
  { side: 'sell', amount: '2.10', coin: 'BTC' },
  { side: 'buy', amount: '55.0', coin: 'ETH' },
  { side: 'sell', amount: '910', coin: 'LIT' },
  { side: 'buy', amount: '0.88', coin: 'BTC' },
  { side: 'sell', amount: '6.40', coin: 'ETH' },
]

function isHeroWord(text) {
  return HERO_WORDS.includes((text || '').replace(/\s+/g, ' ').trim())
}

function cycleHeroWords(root) {
  const nodes = [
    ...root.querySelectorAll(
      '.framer-xn0k8-container h1, .framer-w2kqqa-container h1'
    ),
  ]
  if (!nodes.length) {
    nodes.push(
      ...[...root.querySelectorAll('h1')].filter((node) =>
        isHeroWord(node.textContent)
      )
    )
  }
  if (!nodes.length) return () => {}

  let index = 0
  const paint = () => {
    const wide = window.innerWidth >= 1200
    const mobile = window.innerWidth < 800
    const size = wide ? 4 : 2.6
    nodes.forEach((node) => {
      const mobileNode = Boolean(node.closest('[data-home-mobile-hero], .framer-1cv9dqr'))
      node.textContent = HERO_WORDS[index]
      node.style.fontWeight = '400'
      node.style.fontSize = `calc(var(--framer-root-font-size, 1rem) * ${size})`
      node.style.fontFamily = '"Geist Mono", monospace'
      node.style.letterSpacing = '-0.04em'
      node.style.lineHeight = mobile || mobileNode ? '1.6em' : '1.1em'
      node.style.textAlign = 'center'
      node.style.color = 'rgb(255, 255, 255)'
      node.style.margin = '0px'
      node.style.textTransform = 'uppercase'
    })
  }
  paint()

  const tick = () => {
    index = (index + 1) % HERO_WORDS.length
    paint()
  }

  const id = window.setInterval(tick, HERO_INTERVAL_MS)
  return () => window.clearInterval(id)
}

function playVideos(root) {
  root.querySelectorAll('video').forEach((video) => {
    video.muted = true
    video.playsInline = true
    video.setAttribute('muted', '')
    video.setAttribute('playsinline', '')
    const play = () => {
      video.play().catch(() => {})
    }
    play()
    video.addEventListener('canplay', play, { once: true })
  })
}

function drawTape(canvas) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}

  const dpr = window.devicePixelRatio || 1
  const resize = () => {
    const rect = canvas.getBoundingClientRect()
    canvas.width = Math.max(1, Math.round(rect.width * dpr))
    canvas.height = Math.max(1, Math.round(rect.height * dpr))
  }
  resize()

  let offset = 0
  let frame = 0
  const rowH = 36

  const render = () => {
    const w = canvas.width
    const h = canvas.height
    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = 'rgba(6, 6, 12, 1)'
    ctx.fillRect(0, 0, w, h)

    ctx.save()
    ctx.scale(dpr, dpr)
    const cssW = w / dpr
    const cssH = h / dpr
    const rows = Math.ceil(cssH / rowH) + 2
    for (let i = 0; i < rows; i += 1) {
      const row = TAPE_ROWS[(i + Math.floor(offset / rowH)) % TAPE_ROWS.length]
      const y = ((i * rowH - (offset % rowH)) )
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)'
      ctx.setLineDash([3, 3])
      ctx.strokeRect(2, y + 2, cssW - 4, rowH - 4)
      ctx.setLineDash([])
      ctx.font = '12px "Geist Mono", "IBM Plex Mono", monospace'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = row.side === 'buy' ? '#7cffb2' : '#ff7a7a'
      ctx.fillText(row.side.toUpperCase(), 10, y + rowH / 2)
      ctx.fillStyle = 'rgba(255,255,255,0.85)'
      ctx.fillText(`${row.amount} ${row.coin}`, 70, y + rowH / 2)
    }
    ctx.restore()
    offset += 0.6
    frame = requestAnimationFrame(render)
  }
  frame = requestAnimationFrame(render)
  window.addEventListener('resize', resize)
  return () => {
    cancelAnimationFrame(frame)
    window.removeEventListener('resize', resize)
  }
}

function drawGrid(canvas) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}
  const dpr = window.devicePixelRatio || 1
  let frame = 0
  let t = 0
  const desktopCircuit = Boolean(canvas.closest('.framer-3eyeaf'))
  const mobileCircuit = Boolean(canvas.closest('[data-home-mobile-circuit]'))

  const resize = () => {
    const rect = canvas.getBoundingClientRect()
    canvas.width = Math.max(1, Math.round(rect.width * dpr))
    canvas.height = Math.max(1, Math.round(rect.height * dpr))
  }
  resize()

  const render = () => {
    const w = canvas.width
    const h = canvas.height
    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = 'rgb(6, 6, 12)'
    ctx.fillRect(0, 0, w, h)

    const cssW = w / dpr
    const cssH = h / dpr
    const ethereumField = Boolean(canvas.closest('.framer-P3nCk'))
    const glyphField =
      !ethereumField &&
      (desktopCircuit || (mobileCircuit && cssW > 180 && cssH > 140))
    const binaryField =
      ethereumField ||
      (!glyphField && (desktopCircuit || (mobileCircuit && cssH > cssW)))

    if (glyphField) {
      const glyphs = '+#x@w*·:%'
      const cell = ((desktopCircuit || mobileCircuit) && cssH < 130 ? 7 : 8) * dpr
      ctx.font = `${Math.max(6, cell * 0.9)}px "Geist Mono", monospace`
      ctx.textBaseline = 'top'
      const cols = Math.ceil(w / cell)
      const rows = Math.ceil(h / cell)
      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const n = (col * 17 + row * 31 + Math.floor(t / 8)) % glyphs.length
          ctx.fillStyle = `rgba(174, 188, 229, ${0.08 + (n % 5) * 0.04})`
          ctx.fillText(glyphs[n], col * cell, row * cell)
        }
      }
    } else if (binaryField) {
      ctx.font = `${Math.max(8, 9 * dpr)}px "Geist Mono", monospace`
      ctx.textBaseline = 'top'
      ctx.fillStyle = ethereumField
        ? 'rgba(174, 188, 229, 0.22)'
        : 'rgba(174, 188, 229, 0.45)'
      const cell = (ethereumField ? 8 : 10) * dpr
      const cols = Math.ceil(w / cell)
      const rows = Math.ceil(h / cell)
      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const bit = (col + row + Math.floor(t / 6)) % 2
          ctx.fillText(String(bit), col * cell, row * cell)
        }
      }
    } else {
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'
      ctx.lineWidth = dpr
      const step = 16 * dpr
      const shift = (t * 0.4) % step
      for (let x = -shift; x < w; x += step) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, h)
        ctx.stroke()
      }
      for (let y = -shift; y < h; y += step) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(w, y)
        ctx.stroke()
      }
    }
    t += 1
    frame = requestAnimationFrame(render)
  }
  frame = requestAnimationFrame(render)
  window.addEventListener('resize', resize)
  return () => {
    cancelAnimationFrame(frame)
    window.removeEventListener('resize', resize)
  }
}

function hydrateCanvases(root) {
  const cleanups = []
  root.querySelectorAll('canvas').forEach((canvas) => {
    const inMatchingEngine = Boolean(
      canvas.closest(
        '.framer-vsxpm5-container, .framer-17b1p0x-container, .framer-1sksigt-container, [data-code-component-plugin-id="84d4c1"]',
      ),
    )
    if (inMatchingEngine) return
    const inCircuit = Boolean(
      canvas.closest('.framer-3eyeaf, [data-home-mobile-circuit]'),
    )
    const h = Number(canvas.getAttribute('height') || 0)
    const w = Number(canvas.getAttribute('width') || 0)
    const isTape = !inCircuit && h > 0 && h <= 180 && w >= 180
    cleanups.push(isTape ? drawTape(canvas) : drawGrid(canvas))
  })
  return () => cleanups.forEach((fn) => fn && fn())
}

function runAppear(root) {
  const animator = createAnimator()
  const reduced =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches === true
  const hash = animator.getActiveVariantHash(BREAKPOINTS)
  requestAnimationFrame(() => {
    animator.animateAppearEffects(
      APPEAR_ANIMATIONS,
      (selector, keyframes, options) => {
        const scoped = selector.startsWith(':')
          ? selector
          : selector
        const node = root.querySelector(scoped) || document.querySelector(selector)
        if (!node) return
        Object.entries(keyframes).forEach(([prop, frames]) => {
          animator.startOptimizedAppearAnimation(
            node,
            prop,
            frames,
            options[prop]
          )
        })
      },
      'data-framer-appear-id',
      '__Appear_Animation_Transform__',
      reduced,
      hash
    )
  })
}

function interceptHomeLinks(root, navigate) {
  const onClick = (event) => {
    const anchor = event.target.closest('a')
    if (!anchor || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }
    const href = anchor.getAttribute('href')
    if (!href) return

    if (href.startsWith('/#')) {
      event.preventDefault()
      const id = href.slice(2)
      const target = document.getElementById(id)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }

    if (href.startsWith('/') && !href.startsWith('//')) {
      event.preventDefault()
      navigate(href)
    }
  }
  root.addEventListener('click', onClick)
  return () => root.removeEventListener('click', onClick)
}

const NAV_VARIANTS = [
  'framer-v-coeseb',
  'framer-v-15xrotc',
  'framer-v-qafvwf',
  'framer-v-1e6hh6a',
  'framer-v-1o81lhg',
]

const MOBILE_MENU_LINKS = [
  { label: 'LIGHTER APPS', href: 'https://lighter.xyz/lighter-apps' },
  { label: 'LIGHTER EXCHANGE', href: 'https://lighter.exchange/' },
  { label: 'ROBINHOOD CHAIN', href: 'https://robinhoodchain.lighter.xyz/' },
  { label: 'DOCUMENTATION', href: 'https://docs.lighter.xyz/' },
  { label: 'WHITEPAPER', href: 'https://assets.lighter.xyz/whitepaper.pdf' },
  { label: 'BRAND KIT', href: 'https://lighter.xyz/brand-kit' },
  { label: 'AUDITS', href: 'https://docs.lighter.xyz/security/security-audits' },
  { label: 'STATUS PAGE', href: 'https://status.lighter.xyz/' },
  { label: 'TESTNET', href: 'https://testnet.app.lighter.xyz/' },
  { label: 'FAQ', href: 'https://lighter.xyz/faq' },
  { label: 'X', href: 'https://x.com/Lighter_xyz' },
  { label: 'DISCORD', href: 'https://discord.gg/lighterxyz' },
  { label: 'TELEGRAM', href: 'https://t.me/lighter_announcements' },
]

const CLOSE_ICON_HTML = `
  <span class="home-mobile-icon home-mobile-icon--close" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 3l18 18M21 3 3 21" stroke="#fff" stroke-width="2" stroke-linecap="round"></path>
    </svg>
  </span>
`

function buildMobileMenu() {
  const nav = document.createElement('nav')
  nav.className = 'home-mobile-menu'
  nav.id = 'home-mobile-menu'
  nav.setAttribute('aria-label', 'Mobile navigation')
  nav.hidden = true

  const links = MOBILE_MENU_LINKS.map(
    (item) =>
      `<a class="home-mobile-menu__link" href="${item.href}"${
        item.href.startsWith('http') ? ' target="_blank" rel="noopener noreferrer"' : ''
      }><span class="home-mobile-menu__link-label">${item.label}</span></a>`
  ).join('')

  nav.innerHTML = `
    <div class="home-mobile-menu__column">
      <div class="home-mobile-menu__links">${links}</div>
      <div class="home-mobile-menu__cta">
        <a class="home-mobile-menu__trade" href="/trade/LIT">
          <span class="home-mobile-menu__trade-label">START TRADING</span>
        </a>
      </div>
      <div class="home-mobile-menu__spacer" aria-hidden="true"></div>
    </div>
  `
  return nav
}

function cloneDesktopCta(root, kind) {
  const source =
    kind === 'start'
      ? root.querySelector('.framer-1ggwtvk .framer-1tfkp7t-container > .framer-uaWA7')
      : root.querySelector('.framer-1ggwtvk .framer-srputt-container > .framer-uaWA7')
  if (!source) return null
  return source.cloneNode(true)
}

function hydrateMobileCtas(root) {
  const shell = root.querySelector('.framer-1cv9dqr .framer-1bxvqrf')
  if (!shell) return

  const exploreSlot = shell.querySelector('[data-home-mobile-explore]')
  const startSlot = shell.querySelector('[data-home-mobile-start]')
  const exploreClone = cloneDesktopCta(root, 'explore')
  const startClone = cloneDesktopCta(root, 'start')

  if (exploreSlot && exploreClone) {
    exploreSlot.replaceChildren(exploreClone)
  }
  if (startSlot && startClone) {
    startSlot.replaceChildren(startClone)
  }
}

const CIRCUIT_TRANSITION = 'background-color 80ms linear, opacity 80ms linear'

function paintCircuitFrame(scope, frame, nodeMap) {
  if (!scope || !frame) return

  const colors = frame.colors || frame
  const opacities = frame.opacities || null
  const vClass = frame.vClass || null

  if (vClass) {
    const doomed = [...scope.classList].filter((c) => c.startsWith('framer-v-'))
    doomed.forEach((c) => scope.classList.remove(c))
    scope.classList.add(vClass)
    if (frame.variantName) {
      scope.setAttribute('data-framer-name', frame.variantName)
    }
  }

  Object.keys(colors).forEach((cls) => {
    const color = colors[cls]
    if (!color) return
    let nodes = nodeMap?.get(cls)
    if (!nodes) {
      nodes = [...scope.querySelectorAll(`.${cls}`)]
      nodeMap?.set(cls, nodes)
    }
    const opacity = opacities?.[cls] || '1'
    nodes.forEach((node) => {
      if (!node.style.transition) node.style.transition = CIRCUIT_TRANSITION
      node.style.setProperty('background-color', color)
      node.style.setProperty('opacity', opacity)
      if (!node.style.borderRadius) node.style.borderRadius = '1px'
      node.style.setProperty('animation', 'none')
    })
  })
}

function buildNodeMap(scope, frames) {
  const map = new Map()
  const keys = new Set()
  frames.forEach((frame) => {
    const colors = frame.colors || frame
    Object.keys(colors).forEach((k) => keys.add(k))
  })
  keys.forEach((cls) => {
    const nodes = [...scope.querySelectorAll(`.${cls}`)]
    if (nodes.length) map.set(cls, nodes)
  })
  return map
}

function runCircuitSequence(scope, frames, frameMs, phaseOffset = 0) {
  if (!scope || !frames?.length) return () => {}
  const nodeMap = buildNodeMap(scope, frames)
  if (!nodeMap.size) return () => {}
  let index = ((phaseOffset % frames.length) + frames.length) % frames.length
  let last = performance.now()
  let raf = 0
  let stopped = false
  let prevColors = null

  const paintDiff = (frame) => {
    const colors = frame.colors || frame
    // First paint: apply everything. Later: only keys that changed.
    const keys =
      prevColors == null
        ? Object.keys(colors)
        : Object.keys(colors).filter((k) => colors[k] !== prevColors[k])
    if (prevColors == null || frame.vClass) {
      paintCircuitFrame(scope, frame, nodeMap)
    } else {
      keys.forEach((cls) => {
        const color = colors[cls]
        if (!color) return
        const nodes = nodeMap.get(cls)
        if (!nodes) return
        const opacity = frame.opacities?.[cls] || '1'
        nodes.forEach((node) => {
          if (!node.style.transition) node.style.transition = CIRCUIT_TRANSITION
          node.style.setProperty('background-color', color)
          node.style.setProperty('opacity', opacity)
        })
      })
    }
    prevColors = colors
  }

  paintDiff(frames[index])

  const tick = (now) => {
    if (stopped) return
    if (now - last >= frameMs) {
      last = now
      index = (index + 1) % frames.length
      paintDiff(frames[index])
    }
    raf = window.requestAnimationFrame(tick)
  }
  raf = window.requestAnimationFrame(tick)
  return () => {
    stopped = true
    window.cancelAnimationFrame(raf)
  }
}

const LIGHTER_MARK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 124 248" width="10" height="16" fill="currentColor" aria-hidden="true"><path d="M53.8113 197.302L0 248V53.4414L53.8113 0V197.302ZM124 197.423L70.1887 248V172.122L124 118.833V197.423Z"/></svg>'

function pulseEthereumLogo(circuit) {
  const logo = circuit.querySelector('.framer-g6w19z-container')
  if (!logo) return () => {}
  logo.style.willChange = 'opacity, transform'
  logo.style.transform = 'translate(-50%, -50%)'
  let raf = 0
  const start = performance.now()
  const tick = (now) => {
    const wave = 0.5 + 0.5 * Math.sin(((now - start) / 1000) * Math.PI * 0.55)
    logo.style.opacity = String(0.62 + 0.38 * wave)
    raf = window.requestAnimationFrame(tick)
  }
  raf = window.requestAnimationFrame(tick)
  return () => window.cancelAnimationFrame(raf)
}

function mountProverStatus(circuit) {
  const cols = [...circuit.querySelectorAll('.framer-bqMUm .framer-1yuauh0')]
  if (!cols.length) return () => {}
  cols.forEach((col) => {
    if (col.querySelector('[data-home-prover-status]')) return
    const original = col.querySelector('.framer-4ec7pi')
    if (original) original.style.setProperty('opacity', '0', 'important')
    const host = document.createElement('div')
    host.dataset.homeProverStatus = '1'
    host.innerHTML =
      '<span data-prover-step="0">verifying...</span>' +
      '<span data-prover-step="1">executing</span>' +
      `<span data-prover-step="2">${LIGHTER_MARK_SVG}</span>`
    col.appendChild(host)
  })
  return () => {
    cols.forEach((col) => {
      col.querySelector('[data-home-prover-status]')?.remove()
      const original = col.querySelector('.framer-4ec7pi')
      if (original) original.style.removeProperty('opacity')
    })
  }
}

function pulseTickWave(nodes, litColor, dimColor, intervalMs, windowSize = 3, paintBackground = false) {
  if (!nodes.length) return () => {}
  nodes.forEach((node) => {
    if (!node.style.transition) node.style.transition = CIRCUIT_TRANSITION
  })
  let head = 0
  const id = window.setInterval(() => {
    nodes.forEach((node, i) => {
      const dist = (i - head + nodes.length) % nodes.length
      const lit = dist < windowSize
      const color = lit ? litColor : dimColor
      node.style.setProperty('background-color', color)
      if (paintBackground) node.style.setProperty('background', color)
      node.style.setProperty('opacity', lit ? '1' : '0.85')
    })
    head = (head + 1) % nodes.length
  }, intervalMs)
  return () => window.clearInterval(id)
}

function pulseConnectorTicks(circuit) {
  const cleanups = []
  const tickLit = 'rgba(174, 188, 229, 0.95)'
  const tickDim = 'rgba(174, 188, 229, 0.5)'
  // Matching engine BUY↔MATCH↔SELL ticks — traveling wave (refs 11–12)
  // Mobile uses .framer-aeromk / .framer-17mpboa; desktop uses .framer-lai863 / .framer-9zdfq4.
  cleanups.push(
    pulseTickWave(
      [
        ...circuit.querySelectorAll(
          '.framer-aeromk > div, .framer-lai863 > div',
        ),
      ],
      tickLit,
      tickDim,
      120,
      4,
    ),
  )
  cleanups.push(
    pulseTickWave(
      [
        ...circuit.querySelectorAll(
          '.framer-17mpboa > div, .framer-9zdfq4 > div',
        ),
      ],
      tickLit,
      tickDim,
      120,
      4,
    ),
  )
  // Vertical seam ticks (8Zg78) — live uses background gradient, not background-color
  circuit.querySelectorAll('.framer-8Zg78').forEach((seam, i) => {
    cleanups.push(
      pulseTickWave(
        [...seam.querySelectorAll('[data-border="true"]')],
        'rgba(174, 188, 229, 0.85)',
        'rgba(174, 188, 229, 0.5)',
        140 + i * 20,
        2,
        true,
      ),
    )
  })
  return () => cleanups.forEach((fn) => fn && fn())
}

function animateCircuitInScope(circuit) {
  if (!circuit) return () => {}
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return () => {}
  }

  const cleanups = []
  const { frameMs } = CIRCUIT_ANIM

  circuit.querySelectorAll('.framer-YwJKl').forEach((node, i) => {
    cleanups.push(
      runCircuitSequence(node, CIRCUIT_ANIM.incoming, frameMs.incoming, i * 2),
    )
  })
  circuit.querySelectorAll('.framer-DlUNe').forEach((node, i) => {
    cleanups.push(
      runCircuitSequence(node, CIRCUIT_ANIM.executor, frameMs.executor, i),
    )
  })
  cleanups.push(mountProverStatus(circuit))
  cleanups.push(pulseEthereumLogo(circuit))
  // Full RISK ENGINE panel (header cells + tick rows) — refs 16 + 18
  // Mobile: .framer-6q2ot; desktop: .framer-b9k554
  circuit.querySelectorAll('.framer-6q2ot, .framer-b9k554').forEach((node, i) => {
    if (CIRCUIT_ANIM.risk?.length) {
      cleanups.push(
        runCircuitSequence(node, CIRCUIT_ANIM.risk, frameMs.risk, i),
      )
    }
  })
  // BATCH_PROVER grid / dither — refs 29–33
  circuit.querySelectorAll('.framer-xc6t19, .framer-5guu0w').forEach((node, i) => {
    if (CIRCUIT_ANIM.prover?.length) {
      cleanups.push(
        runCircuitSequence(node, CIRCUIT_ANIM.prover, frameMs.prover, i * 2),
      )
    }
  })
  cleanups.push(pulseConnectorTicks(circuit))

  return () => cleanups.forEach((fn) => fn && fn())
}

function animateMobileCircuit(root) {
  return animateCircuitInScope(root.querySelector('[data-home-mobile-circuit]'))
}

function animateDesktopCircuit(root) {
  const circuit = root.querySelector('.framer-3eyeaf')
  if (!circuit) return () => {}

  const mq = window.matchMedia('(min-width: 1200px)')
  let stop = () => {}

  const sync = () => {
    stop()
    stop = () => {}
    if (!mq.matches) return
    circuit.dataset.circuitAnim = '1'
    const innerStop = animateCircuitInScope(circuit)
    stop = () => {
      innerStop()
      delete circuit.dataset.circuitAnim
    }
  }

  sync()
  mq.addEventListener('change', sync)
  return () => {
    mq.removeEventListener('change', sync)
    stop()
  }
}

function mountMobileCircuit(root) {
  const column = root.querySelector('.framer-1hu3y0')
  if (!column) return () => {}

  column
    .querySelectorAll(
      '[data-home-mobile-circuit] [data-code-component-plugin-id="84d4c1"] span',
    )
    .forEach((span) => {
      const text = (span.textContent || '').trim()
      if (text === 'BUY') span.style.color = 'rgba(95, 224, 172, 0.5)'
      if (text === 'SELL') span.style.color = 'rgba(224, 95, 95, 0.5)'
    })

  const circuit = column.querySelector('[data-home-mobile-circuit]')
  if (!circuit) return () => {}

  let stopAnim = () => {}
  if (circuit.dataset.circuitAnim !== '1') {
    circuit.dataset.circuitAnim = '1'
    stopAnim = animateMobileCircuit(root)
  }

  return () => {
    stopAnim()
    delete circuit.dataset.circuitAnim
  }
}

function mountMobileChrome(root) {
  const nav = root.querySelector('[data-home-mobile-nav]')
  const burger = nav?.querySelector('.framer-1ovhzyo')
  if (!nav || !burger) {
    return { burger: null, menu: null, unmount: () => {} }
  }

  if (!burger.querySelector('.home-mobile-icon--close')) {
    burger.insertAdjacentHTML('beforeend', CLOSE_ICON_HTML)
  }
  burger.setAttribute('role', 'button')
  burger.setAttribute('tabindex', '0')
  burger.setAttribute('aria-label', 'Open navigation menu')
  burger.setAttribute('aria-controls', 'home-mobile-menu')

  const menu = buildMobileMenu()
  root.appendChild(menu)

  return {
    burger,
    menu,
    unmount: () => {
      burger.querySelectorAll('.home-mobile-icon').forEach((node) => node.remove())
      burger.removeAttribute('role')
      burger.removeAttribute('tabindex')
      burger.removeAttribute('aria-label')
      burger.removeAttribute('aria-controls')
      burger.removeAttribute('aria-expanded')
      menu.remove()
    },
  }
}

const WIDE_SELECTORS = [
  '.framer-AQ3ek.framer-sgcc88',
  '.framer-VaM6o.framer-fe1qqo',
  '.framer-pX7ys.framer-10z24rs',
  '.framer-c2r8l.framer-c5v4g7',
  '.framer-Yw8UE.framer-1dmgraw',
  '.framer-sniXn.framer-juqpbx',
  '.framer-9Tdy8.framer-1o81l6a',
]

function setNavVariant(header, next) {
  if (!header) return
  NAV_VARIANTS.forEach((name) => header.classList.remove(name))
  header.classList.add(next)
}

function applyResponsiveRoot(root) {
  const page = root.querySelector('[data-framer-root]')
  if (!page) return () => {}

  const header = root.querySelector('.framer-13bm1dq-container .framer-AQ3ek')
  const chrome = mountMobileChrome(root)
  const burger = chrome.burger
  let menuOpen = false
  let previousOverflow = ''

  const setMenuOpen = (next) => {
    menuOpen = next
    document.documentElement.setAttribute(
      'data-home-menu',
      menuOpen ? 'open' : 'closed'
    )
    if (chrome.menu) {
      chrome.menu.hidden = !menuOpen
      chrome.menu.setAttribute('aria-hidden', menuOpen ? 'false' : 'true')
    }
    if (burger) {
      burger.setAttribute('aria-expanded', menuOpen ? 'true' : 'false')
      burger.setAttribute(
        'aria-label',
        menuOpen ? 'Close navigation menu' : 'Open navigation menu'
      )
    }
    if (menuOpen) {
      previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = previousOverflow
    }
  }

  const constrainWide = () => {
    WIDE_SELECTORS.forEach((selector) => {
      root.querySelectorAll(selector).forEach((node) => {
        node.style.setProperty('width', '100%', 'important')
        node.style.setProperty('max-width', '100%', 'important')
        node.style.setProperty('min-width', '0', 'important')
      })
    })
  }

  const pinNav = () => {
    root.querySelectorAll('.framer-13bm1dq-container, .framer-1gvf5p5-container').forEach((node) => {
      node.style.setProperty('position', 'sticky', 'important')
      node.style.setProperty('top', '0px', 'important')
      node.style.setProperty('z-index', '50', 'important')
      node.style.setProperty('transform', 'none', 'important')
      node.style.removeProperty('will-change')
    })
  }

  const pinHeroViewport = () => {
    if (window.innerWidth < 800) {
      document.documentElement.style.removeProperty('--home-nav-h')
      return
    }
    const bar = root.querySelector('.framer-AQ3ek')
    const measured = bar ? Math.round(bar.getBoundingClientRect().height) : 68
    const navH = measured >= 40 && measured <= 120 ? measured : 68
    document.documentElement.style.setProperty('--home-nav-h', `${navH}px`)
  }

  const sync = () => {
    const width = window.innerWidth
    root.style.maxWidth = '100%'
    root.style.minWidth = '0'
    root.style.width = '100%'
    page.style.setProperty('width', '100%', 'important')
    page.style.setProperty('max-width', '100%', 'important')
    page.style.setProperty('min-width', '0', 'important')
    constrainWide()
    pinNav()
    pinHeroViewport()
    window.requestAnimationFrame(pinHeroViewport)

    if (width < 800) {
      const ctaWidth = `${Math.max(240, width - 32)}px`
      root.querySelectorAll('.framer-pX7ys, .framer-18p90h7, .framer-fhpbhb').forEach((node) => {
        if (node.closest('[data-home-mobile-start-trading]')) return
        node.style.setProperty('width', ctaWidth, 'important')
        node.style.setProperty('max-width', ctaWidth, 'important')
        node.style.setProperty('min-width', '0', 'important')
      })
      root.querySelectorAll(
        '.framer-1ggwtvk .framer-19bp0by, .framer-1ggwtvk .framer-2ged9r'
      ).forEach((node) => {
        node.style.setProperty('flex-flow', 'column nowrap', 'important')
        node.style.setProperty('flex-direction', 'column', 'important')
        node.style.setProperty('align-items', 'stretch', 'important')
        node.style.setProperty('width', '100%', 'important')
        node.style.setProperty('max-width', '100%', 'important')
        node.style.setProperty('gap', '12px', 'important')
      })
      root.querySelectorAll(
        '.framer-1ggwtvk .framer-uaWA7, .framer-1ggwtvk .framer-P1B8M'
      ).forEach((node) => {
        node.style.setProperty('width', '100%', 'important')
        node.style.setProperty('max-width', '100%', 'important')
      })
    } else if (width < 1200) {
      if (menuOpen) setMenuOpen(false)
      root.querySelectorAll('.framer-pX7ys, .framer-18p90h7, .framer-fhpbhb').forEach((node) => {
        node.style.removeProperty('width')
        node.style.removeProperty('max-width')
        node.style.removeProperty('min-width')
      })
      setNavVariant(header, 'framer-v-qafvwf')
    } else {
      if (menuOpen) setMenuOpen(false)
      root.querySelectorAll('.framer-pX7ys, .framer-18p90h7, .framer-fhpbhb').forEach((node) => {
        node.style.removeProperty('width')
        node.style.removeProperty('max-width')
        node.style.removeProperty('min-width')
      })
      setNavVariant(header, 'framer-v-coeseb')
    }
  }

  const onBurger = (event) => {
    if (window.innerWidth >= 800) return
    event.preventDefault()
    event.stopPropagation()
    setMenuOpen(!menuOpen)
  }

  const onBurgerKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    onBurger(event)
  }

  const onMenuClick = (event) => {
    if (event.target.closest('a')) setMenuOpen(false)
  }

  const onKey = (event) => {
    if (event.key === 'Escape' && menuOpen) setMenuOpen(false)
  }

  sync()
  setMenuOpen(false)
  window.addEventListener('resize', sync)
  window.addEventListener('keydown', onKey)
  burger?.addEventListener('click', onBurger)
  burger?.addEventListener('keydown', onBurgerKey)
  chrome.menu?.addEventListener('click', onMenuClick)
  return () => {
    window.removeEventListener('resize', sync)
    window.removeEventListener('keydown', onKey)
    burger?.removeEventListener('click', onBurger)
    burger?.removeEventListener('keydown', onBurgerKey)
    chrome.menu?.removeEventListener('click', onMenuClick)
    chrome.unmount()
    document.documentElement.removeAttribute('data-home-menu')
    document.body.style.overflow = previousOverflow
    page.style.removeProperty('width')
    page.style.removeProperty('max-width')
    page.style.removeProperty('min-width')
    document.documentElement.style.removeProperty('--home-nav-h')
    setNavVariant(header, 'framer-v-coeseb')
  }
}

function revealFrozenAppears(root) {
  const frozen = root.querySelectorAll('.framer-162dzzp, .framer-19vtrv9')
  frozen.forEach((node, index) => {
    node.style.willChange = 'opacity, transform'
    node.style.transition = 'opacity 0.55s ease, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)'
    node.style.transitionDelay = `${0.12 * index}s`
    requestAnimationFrame(() => {
      node.style.opacity = '1'
      node.style.transform = 'none'
    })
  })
}

export function runHomeEnhancements(root, navigate) {
  if (!root) return () => {}
  const cleanups = []
  try {
    hydrateMobileCtas(root)
    cleanups.push(mountMobileCircuit(root))
  } catch (error) {
    console.warn('home mobile header', error)
  }
  const stopWidth = applyResponsiveRoot(root)
  cleanups.push(stopWidth)
  try {
    bindNestedLinks(root, navigate)
  } catch (error) {
    console.warn('home nested links', error)
  }
  try {
    playVideos(root)
  } catch (error) {
    console.warn('home videos', error)
  }
  try {
    cleanups.push(cycleHeroWords(root))
  } catch (error) {
    console.warn('home hero', error)
  }
  try {
    cleanups.push(hydrateCanvases(root))
  } catch (error) {
    console.warn('home canvas', error)
  }
  try {
    cleanups.push(animateDesktopCircuit(root))
  } catch (error) {
    console.warn('home circuit', error)
  }
  try {
    cleanups.push(interceptHomeLinks(root, navigate))
  } catch (error) {
    console.warn('home links', error)
  }
  try {
    revealFrozenAppears(root)
  } catch (error) {
    console.warn('home appear', error)
  }
  return () => cleanups.forEach((fn) => fn && fn())
}

if (import.meta.hot) {
  import.meta.hot.accept('./circuit-anim-frames.js', () => {
    import.meta.hot.invalidate()
  })
}
